/**
 * Notification Bell Component
 *
 * Displays a notification bell with unread count badge and dropdown
 * for viewing recent notifications across all apps.
 */

import { LitElement, html, css, PropertyValues } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import type { Notification } from '../types';
import { ApiError, notificationsAPI } from '../services/api';
import { wsService } from '../services/websocket';
import { eventBus, Events } from '../services/events';

/**
 * Turn a load failure into something a person can act on.
 *
 * The bell used to swallow every one of these into a console line, leaving
 * the dropdown showing a calm "No notifications" and a status row reading
 * "Connecting...". Three quite different faults -- an expired session, a base
 * URL with no notification routes on it, and an actually-unreachable service
 * -- were indistinguishable in the UI, which is why the misrouting took so
 * long to find.
 */
function describeLoadFailure(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 401 || e.status === 403) {
      return 'Your session has expired. Sign in again to see notifications.';
    }
    if (e.status === 404) {
      return `No notification service at ${e.url}. The app may be misconfigured.`;
    }
    if (e.status >= 500) {
      return 'The notification service is not responding. Try again shortly.';
    }
    return e.message;
  }
  // fetch() rejects with a TypeError only on a genuine transport failure --
  // DNS, TLS, CORS, offline. That is the one case that really is "cannot
  // connect", as opposed to "connected fine and was told no".
  return 'Could not reach the notification service.';
}

@customElement('moya-notification-bell')
export class MoyaNotificationBell extends LitElement {
  static styles = css`
    :host {
      display: inline-block;
      position: relative;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    }

    .trigger {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      border: none;
      background: transparent;
      border-radius: 8px;
      cursor: pointer;
      color: currentColor;
      transition: background-color 0.15s ease;
      position: relative;
    }

    .trigger:hover {
      background: rgba(255, 255, 255, 0.1);
    }

    /* Visible keyboard focus indicator (WCAG 2.4.7) */
    .trigger:focus-visible {
      outline: 2px solid #60a5fa;
      outline-offset: 2px;
      box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.5);
    }

    .trigger.open {
      background: rgba(255, 255, 255, 0.15);
    }

    .bell-icon {
      width: 20px;
      height: 20px;
    }

    .badge {
      position: absolute;
      top: 4px;
      right: 4px;
      min-width: 16px;
      height: 16px;
      padding: 0 4px;
      background: #ef4444;
      border-radius: 8px;
      font-size: 10px;
      font-weight: 600;
      color: white;
      display: flex;
      align-items: center;
      justify-content: center;
      line-height: 1;
    }

    .badge.large {
      min-width: 20px;
      font-size: 9px;
    }

    /* Visually-hidden span used for aria-live announcements */
    .sr-only {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    }

    .dropdown {
      position: absolute;
      top: calc(100% + 8px);
      right: 0;
      width: 360px;
      /* Never wider than the viewport. A fixed 360px anchored to the bell
         hangs off the left edge on a narrow window, which reads as the panel
         being "off centre" -- it is actually overflowing. */
      max-width: calc(100vw - 24px);
      max-height: min(480px, calc(100vh - 96px));
      background: #1e293b;
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 12px;
      box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
      overflow: hidden;
      z-index: 1000;
      display: none;
    }

    .dropdown.open {
      display: block;
    }

    /* On a phone the panel cannot hang off the bell at all.

       The bell is NOT the rightmost thing in the header -- the user menu is
       -- so "right: 0" pins the panel's right edge about 56px in from the
       screen edge, and a 360px panel then starts 56px off the left of the
       screen. Clamping the width does not fix that: the panel is
       mispositioned, not oversized, which is why the max-width of
       calc(100vw - 24px) above still left it looking off-centre. An earlier
       attempt set left: 12px and right: 12px under a 420px breakpoint, which
       stretches the panel to the full screen width, and whose breakpoint sits
       within a few px of the widths where the overflow actually begins.

       So: take it out of the bell's flow entirely and centre it on the
       viewport. Placement then no longer depends on where in the header the
       bell happens to sit, which is the part that kept breaking. Note that
       position: fixed resolves against the viewport here only because no
       ancestor sets transform, filter, backdrop-filter or contain -- worth
       keeping true, since any one of those on the header would silently
       re-anchor this panel.

       Keep backticks out of this comment: the whole stylesheet is a tagged
       template literal, so one would end the literal mid-rule and the file
       stops parsing. */
    @media (max-width: 640px) {
      .dropdown {
        position: fixed;
        top: 60px; /* header is 56px tall, plus the same 4px gap as desktop */
        left: 50%;
        right: auto;
        transform: translateX(-50%);
        width: min(360px, calc(100vw - 24px));
        max-width: none;
        max-height: calc(100vh - 80px);
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .dropdown,
      .spinner {
        animation: none !important;
        transition: none !important;
      }
    }

    .header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 12px 16px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    }

    .header-title {
      font-size: 14px;
      font-weight: 600;
      color: white;
    }

    .mark-all-read {
      background: none;
      border: none;
      color: #60a5fa;
      font-size: 12px;
      cursor: pointer;
      padding: 4px 8px;
      border-radius: 4px;
      transition: background-color 0.15s ease;
    }

    .mark-all-read:hover {
      background: rgba(96, 165, 250, 0.1);
    }

    .mark-all-read:focus-visible {
      outline: 2px solid #60a5fa;
      outline-offset: 2px;
    }

    .mark-all-read:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .notifications-list {
      max-height: 360px;
      overflow-y: auto;
    }

    .notifications-list::-webkit-scrollbar {
      width: 6px;
    }

    .notifications-list::-webkit-scrollbar-track {
      background: transparent;
    }

    .notifications-list::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 255, 0.2);
      border-radius: 3px;
    }

    .notification-item {
      display: flex;
      gap: 12px;
      padding: 12px 16px;
      cursor: pointer;
      transition: background-color 0.15s ease;
      border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    }

    .notification-item:hover {
      background: rgba(255, 255, 255, 0.05);
    }

    .notification-item:focus-visible {
      outline: 2px solid #60a5fa;
      outline-offset: -2px;
      background: rgba(255, 255, 255, 0.08);
    }

    .notification-item:last-child {
      border-bottom: none;
    }

    .notification-item.unread {
      background: rgba(59, 130, 246, 0.05);
    }

    .notification-item.unread::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 3px;
      background: #3b82f6;
    }

    .notification-icon {
      flex-shrink: 0;
      width: 36px;
      height: 36px;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 14px;
    }

    .notification-icon.alert {
      background: rgba(239, 68, 68, 0.2);
      color: #ef4444;
    }

    .notification-icon.warning {
      background: rgba(245, 158, 11, 0.2);
      color: #f59e0b;
    }

    .notification-icon.success {
      background: rgba(16, 185, 129, 0.2);
      color: #10b981;
    }

    .notification-icon.info {
      background: rgba(59, 130, 246, 0.2);
      color: #3b82f6;
    }

    .notification-content {
      flex: 1;
      min-width: 0;
    }

    .notification-title {
      font-size: 13px;
      font-weight: 500;
      color: white;
      margin-bottom: 2px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .notification-body {
      font-size: 12px;
      color: rgba(255, 255, 255, 0.6);
      margin-bottom: 4px;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    .notification-meta {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 11px;
      color: rgba(255, 255, 255, 0.4);
    }

    .notification-source {
      padding: 2px 6px;
      background: rgba(255, 255, 255, 0.1);
      border-radius: 4px;
    }

    .empty-state {
      padding: 40px 24px;
      text-align: center;
    }

    .empty-icon {
      font-size: 32px;
      margin-bottom: 12px;
      opacity: 0.5;
    }

    .empty-text {
      color: rgba(255, 255, 255, 0.5);
      font-size: 14px;
    }

    .footer {
      padding: 12px;
      border-top: 1px solid rgba(255, 255, 255, 0.1);
    }

    .view-all {
      display: block;
      width: 100%;
      padding: 8px;
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 8px;
      color: rgba(255, 255, 255, 0.8);
      font-size: 13px;
      font-weight: 500;
      text-align: center;
      text-decoration: none;
      cursor: pointer;
      transition: background-color 0.15s ease;
    }

    .view-all:hover {
      background: rgba(255, 255, 255, 0.1);
    }

    .view-all:focus-visible {
      outline: 2px solid #60a5fa;
      outline-offset: 2px;
    }

    .loading {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 40px;
    }

    .spinner {
      width: 24px;
      height: 24px;
      border: 2px solid rgba(255, 255, 255, 0.1);
      border-top-color: #60a5fa;
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
    }

    @keyframes spin {
      to {
        transform: rotate(360deg);
      }
    }

    .connection-status {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 8px 16px;
      font-size: 11px;
      color: rgba(255, 255, 255, 0.5);
      background: rgba(255, 255, 255, 0.03);
    }

    .status-dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
    }

    .status-dot.connected {
      background: #10b981;
    }

    .status-dot.disconnected {
      background: #ef4444;
    }

    .status-dot.idle {
      background: rgba(255, 255, 255, 0.35);
    }

    .load-error {
      display: flex;
      gap: 8px;
      align-items: flex-start;
      padding: 16px;
      margin: 12px;
      border: 1px solid rgba(239, 68, 68, 0.4);
      background: rgba(239, 68, 68, 0.1);
      border-radius: 8px;
      color: rgba(255, 255, 255, 0.85);
      font-size: 12px;
      line-height: 1.5;
    }

    .load-error-icon {
      flex-shrink: 0;
      color: #ef4444;
      font-size: 14px;
      line-height: 1.2;
    }
  `;

  @property({ type: String }) token = '';
  @property({ type: String }) notificationsUrl = '';

  /**
   * Where "View all notifications" goes. Absolute URL.
   *
   * This used to be a hardcoded `href="/notifications"` -- a HOST-RELATIVE
   * path. The shell is embedded in several apps, and it resolved against
   * whichever one was hosting it. In Messenger that is
   * messenger.moyasolutions.com/notifications, which is not a route the app
   * has: it fell through the router and rendered a BLANK PAGE.
   *
   * Unset means the link is not rendered at all. A missing link is honest; a
   * link that goes nowhere is worse than none, and it is the one thing a user
   * clicks when they want more than the last ten.
   */
  @property({ type: String }) notificationsAppUrl = '';

  @state() private isOpen = false;
  @state() private notifications: Notification[] = [];
  @state() private unreadCount = 0;
  @state() private loading = false;
  @state() private wsConnected = false;
  @state() private loadError: string | null = null;

  @query('.trigger') private triggerEl!: HTMLButtonElement;
  @query('.dropdown') private dropdownEl!: HTMLDivElement;

  private clickOutsideHandler: ((e: MouseEvent) => void) | null = null;
  private wsUnsubscribers: (() => void)[] = [];

  connectedCallback() {
    super.connectedCallback();
    this.clickOutsideHandler = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.clickOutsideHandler);
    this.setupWebSocket();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.clickOutsideHandler) {
      document.removeEventListener('click', this.clickOutsideHandler);
    }
    this.teardownWebSocket();
  }

  /**
   * Drop the socket subscriptions this element owns.
   *
   * Factored out of disconnectedCallback so re-setup (token or URL arriving
   * after mount) cannot stack a second set of handlers on top of the first.
   * Without this, every re-setup would add another onNotification listener
   * and each incoming notification would be prepended once per listener.
   */
  private teardownWebSocket() {
    this.wsUnsubscribers.forEach((unsub) => unsub());
    this.wsUnsubscribers = [];
  }

  protected updated(changedProperties: PropertyValues) {
    // THE BUG THIS FIXES (2026-09-19). setupWebSocket() ran exactly once, from
    // connectedCallback(). Lit upgrades this element when it is created; the
    // host app assigns `.token` and `.notificationsUrl` AFTER that. So setup
    // ran with an empty token, `configure()` only stores a token when one is
    // truthy, and `connect()` returns early at `if (!this.token)`. Nothing
    // ever retried, because nothing watched these two properties.
    //
    // The visible result was the worst kind: the REST calls have their own
    // client and kept working, so the panel FILLED WITH NOTIFICATIONS while
    // the status row said "Reconnecting…" forever, and the server logged not
    // one connection attempt. Live updates were never coming.
    if (
      (changedProperties.has('token') || changedProperties.has('notificationsUrl')) &&
      this.notificationsUrl &&
      this.token
    ) {
      this.teardownWebSocket();
      this.setupWebSocket();
    }

    if (changedProperties.has('isOpen') && this.isOpen) {
      this.loadNotifications();
      // Focus first notification (or mark-all-read button) when dropdown opens
      requestAnimationFrame(() => {
        const first = this.dropdownEl?.querySelector<HTMLElement>('[role="menuitem"], .mark-all-read');
        first?.focus();
      });
    }
  }

  private setupWebSocket() {
    // Subscribe to WebSocket events
    this.wsUnsubscribers.push(
      wsService.onNotification((notification) => {
        // Add new notification to the top
        this.notifications = [notification, ...this.notifications.slice(0, 9)];
        this.unreadCount++;

        // Emit event
        eventBus.emit(Events.NOTIFICATION_RECEIVED, notification);
      })
    );

    this.wsUnsubscribers.push(
      wsService.onUnreadCount((count) => {
        this.unreadCount = count;
        eventBus.emit(Events.UNREAD_COUNT_CHANGED, count);
      })
    );

    this.wsUnsubscribers.push(
      wsService.onConnectionChange((connected) => {
        this.wsConnected = connected;
      })
    );

    // Connect to WebSocket
    if (this.notificationsUrl) {
      wsService.configure(this.notificationsUrl, this.token);
      wsService.connect();
    }
  }

  private handleClickOutside(e: MouseEvent) {
    if (this.isOpen && !this.contains(e.target as Node)) {
      this.isOpen = false;
    }
  }

  private async loadNotifications() {
    if (this.loading) return;

    this.loading = true;

    try {
      const [notifications, countResult] = await Promise.all([
        notificationsAPI.getNotifications({ limit: 10 }),
        notificationsAPI.getUnreadCount(),
      ]);

      this.notifications = notifications;
      this.unreadCount = countResult.count;
      this.loadError = null;
    } catch (e) {
      this.loadError = describeLoadFailure(e);
      console.error(
        `[NotificationBell] Failed to load notifications from ${
          e instanceof ApiError ? e.url : '(unconfigured)'
        }:`,
        e
      );
    } finally {
      this.loading = false;
    }
  }

  private toggleDropdown(e: Event) {
    e.stopPropagation();
    this.isOpen = !this.isOpen;
  }

  private handleTriggerKeydown(e: KeyboardEvent) {
    if ((e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') && !this.isOpen) {
      e.preventDefault();
      this.isOpen = true;
    } else if (e.key === 'Escape' && this.isOpen) {
      e.preventDefault();
      this.isOpen = false;
    }
  }

  private handleMenuKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      if (this.isOpen) {
        e.preventDefault();
        this.isOpen = false;
        this.triggerEl?.focus();
      }
      return;
    }

    if (!this.dropdownEl) return;
    const items = Array.from(
      this.dropdownEl.querySelectorAll<HTMLElement>('[role="menuitem"]')
    );
    if (items.length === 0) return;

    // Focus lives inside the shadow root, so document.activeElement returns the
    // host element (always -1 here). Read the shadow root's activeElement so
    // roving focus actually steps through the menu items.
    const active = this.shadowRoot?.activeElement as HTMLElement | null;
    const currentIndex = active ? items.indexOf(active) : -1;

    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        const next = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
        items[next]?.focus();
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        const prev = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
        items[prev]?.focus();
        break;
      }
      case 'Home':
        e.preventDefault();
        items[0]?.focus();
        break;
      case 'End':
        e.preventDefault();
        items[items.length - 1]?.focus();
        break;
    }
  }

  private async handleNotificationClick(notification: Notification) {
    // Mark as read
    if (!notification.read) {
      try {
        await notificationsAPI.markAsRead(notification.id);
        notification.read = true;
        this.unreadCount = Math.max(0, this.unreadCount - 1);
        this.requestUpdate();

        eventBus.emit(Events.NOTIFICATION_READ, notification);
      } catch (e) {
        console.error('[NotificationBell] Failed to mark as read:', e);
      }
    }

    // Dispatch event
    this.dispatchEvent(
      new CustomEvent('moya-notification-click', {
        bubbles: true,
        composed: true,
        detail: { notification },
      })
    );

    eventBus.emit(Events.NOTIFICATION_CLICK, notification);

    // Navigate to action URL if present
    if (notification.actionUrl) {
      window.location.href = notification.actionUrl;
    }

    this.isOpen = false;
  }

  private async handleMarkAllRead() {
    try {
      await notificationsAPI.markAllAsRead();
      this.notifications = this.notifications.map((n) => ({ ...n, read: true }));
      this.unreadCount = 0;
    } catch (e) {
      console.error('[NotificationBell] Failed to mark all as read:', e);
    }
  }

  private formatTime(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '';
    const now = new Date();
    const diff = now.getTime() - date.getTime();

    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;

    return date.toLocaleDateString();
  }

  private getIconClass(notification: Notification): string {
    const priority = notification.priority;
    if (priority === 'urgent' || priority === 'high') return 'alert';
    if (notification.type?.includes('warning')) return 'warning';
    if (notification.type?.includes('success')) return 'success';
    return 'info';
  }

  private getIcon(notification: Notification): string {
    const priority = notification.priority;
    if (priority === 'urgent' || priority === 'high') return '⚠';
    if (notification.type?.includes('warning')) return '⚡';
    if (notification.type?.includes('success')) return '✓';
    return 'ℹ';
  }

  /**
   * Real-time status, honestly reported.
   *
   * The old label was a flat `wsConnected ? active : 'Connecting...'`, so a
   * bell that had never been given a notifications URL -- and therefore never
   * called connect() even once -- sat on "Connecting..." forever. That is the
   * single most misleading string in this component: nothing was connecting.
   */
  /**
   * Where "View all notifications" points, or '' to omit the link.
   *
   * Explicit `notificationsAppUrl` wins. Otherwise this derives the ORIGIN of
   * `notificationsUrl`, because the notification dashboard is served at `/`
   * on the same host that serves the API under `/api` -- verified against the
   * live ingress, which routes `/api`, `/ws` and `/health` to the service and
   * `/` to the dashboard.
   *
   * Deriving rather than hardcoding means the link follows whatever host the
   * app already points the bell at, including staging. A malformed URL yields
   * '' and the link is omitted, which is the honest outcome: the old
   * hardcoded `/notifications` was host-relative and rendered a blank page in
   * every app that lacks that route.
   */
  private get viewAllHref(): string {
    if (this.notificationsAppUrl) return this.notificationsAppUrl;
    if (!this.notificationsUrl) return '';
    try {
      return new URL(this.notificationsUrl).origin + '/';
    } catch {
      return '';
    }
  }

  private get connectionState(): 'connected' | 'disconnected' | 'idle' {
    if (!this.notificationsUrl) return 'idle';
    return this.wsConnected ? 'connected' : 'disconnected';
  }

  private get connectionLabel(): string {
    switch (this.connectionState) {
      case 'connected':
        return 'Real-time updates active';
      case 'idle':
        return 'Live updates unavailable';
      default:
        return 'Reconnecting…';
    }
  }

  private renderNotification(notification: Notification) {
    const itemClasses = classMap({
      'notification-item': true,
      unread: !notification.read,
    });

    const iconClasses = classMap({
      'notification-icon': true,
      [this.getIconClass(notification)]: true,
    });

    const readState = notification.read ? '' : ' (unread)';
    const ariaLabel = `${notification.title}${readState}${notification.body ? ' — ' + notification.body : ''}`;

    return html`
      <div
        class="${itemClasses}"
        @click="${() => this.handleNotificationClick(notification)}"
        role="menuitem"
        tabindex="-1"
        aria-label="${ariaLabel}"
        @keydown="${(e: KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.handleNotificationClick(notification);
          }
        }}"
      >
        <div class="${iconClasses}" aria-hidden="true">${this.getIcon(notification)}</div>
        <div class="notification-content">
          <div class="notification-title">${notification.title}</div>
          ${notification.body
            ? html`<div class="notification-body">${notification.body}</div>`
            : ''}
          <div class="notification-meta">
            <span>${this.formatTime(notification.createdAt)}</span>
            ${notification.sourceApp
              ? html`<span class="notification-source">${notification.sourceApp}</span>`
              : ''}
          </div>
        </div>
      </div>
    `;
  }

  render() {
    const triggerClasses = classMap({
      trigger: true,
      open: this.isOpen,
    });

    const dropdownClasses = classMap({
      dropdown: true,
      open: this.isOpen,
    });

    const badgeClasses = classMap({
      badge: true,
      large: this.unreadCount > 99,
    });

    const unreadLabel = this.unreadCount === 0
      ? 'No unread notifications'
      : this.unreadCount === 1
        ? '1 unread notification'
        : `${this.unreadCount > 99 ? '99+' : this.unreadCount} unread notifications`;

    return html`
      <button
        class="${triggerClasses}"
        @click="${this.toggleDropdown}"
        @keydown="${this.handleTriggerKeydown}"
        aria-label="Notifications — ${unreadLabel}"
        aria-expanded="${this.isOpen ? 'true' : 'false'}"
        aria-haspopup="menu"
        aria-controls="moya-notification-bell-menu"
      >
        <svg
          class="bell-icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          aria-hidden="true"
        >
          <path
            d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        ${this.unreadCount > 0
          ? html`<span class="${badgeClasses}" aria-hidden="true">${this.unreadCount > 99 ? '99+' : this.unreadCount}</span>`
          : ''}
      </button>

      <!-- Polite live-region announces unread-count changes to screen readers -->
      <span class="sr-only" aria-live="polite" aria-atomic="true">${unreadLabel}</span>

      <div
        id="moya-notification-bell-menu"
        class="${dropdownClasses}"
        role="menu"
        aria-label="Notifications"
        @keydown="${this.handleMenuKeydown}"
      >
        <div class="header">
          <span class="header-title" id="moya-notifications-heading">Notifications</span>
          <button
            class="mark-all-read"
            @click="${this.handleMarkAllRead}"
            ?disabled="${this.unreadCount === 0}"
            aria-label="Mark all notifications as read"
          >
            Mark all read
          </button>
        </div>

        <div class="connection-status" role="status" aria-live="polite">
          <span class="status-dot ${this.connectionState}" aria-hidden="true"></span>
          <span>${this.connectionLabel}</span>
        </div>

        ${this.loading
          ? html`
              <div class="loading" role="status" aria-live="polite" aria-label="Loading notifications">
                <div class="spinner"></div>
              </div>
            `
          : this.notifications.length > 0
          ? html`
              <div class="notifications-list" role="group" aria-labelledby="moya-notifications-heading">
                ${this.notifications.map((n) => this.renderNotification(n))}
              </div>
            `
          : this.loadError
          ? html`
              <div class="load-error" role="alert">
                <span class="load-error-icon" aria-hidden="true">⚠</span>
                <span>${this.loadError}</span>
              </div>
            `
          : html`
              <div class="empty-state">
                <div class="empty-icon" aria-hidden="true">🔔</div>
                <div class="empty-text">No notifications</div>
              </div>
            `}

        <div class="footer">
          ${this.viewAllHref
            ? html`<a
                href="${this.viewAllHref}"
                class="view-all"
                role="menuitem"
                tabindex="-1"
              >
                View all notifications
              </a>`
            : ''}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'moya-notification-bell': MoyaNotificationBell;
  }
}
